/**
 * Shared constants for the ABC/XYZ Matrix panel.
 *
 * Business-friendly wording is the primary presentation (High/Medium/Low
 * Impact × Stable/Varying/Sporadic); the raw ABC/XYZ notation stays visible
 * in badges and tooltips for users familiar with the framework.
 */

export const ANALYSIS_TYPE_VARIANT = "variant";
export const ANALYSIS_TYPE_OBJECT = "object";
export const ANALYSIS_TYPE_CROSS = "cross";

export const ANALYSIS_TYPE_OPTIONS = [
  { id: ANALYSIS_TYPE_VARIANT, label: "Variant Matrix" },
  { id: ANALYSIS_TYPE_OBJECT, label: "Object Matrix" },
  { id: ANALYSIS_TYPE_CROSS, label: "Cross Matrix" },
];

// id -> label, so the grid heading and the config panel title never restate a
// mode name that lives in ANALYSIS_TYPE_OPTIONS.
export const ANALYSIS_TYPE_LABELS = Object.fromEntries(
  ANALYSIS_TYPE_OPTIONS.map((option) => [option.id, option.label]),
);

export const DEFAULT_TITLE = "ABC/XYZ Matrix";

export const VARIANT_MODE_OPTIONS = [
  { id: "exact", label: "Exact" },
  { id: "compressed", label: "Compressed" },
  { id: "loop_compressed", label: "Loop-compressed" },
];

export const ABC_AGGREGATION_OPTIONS = [
  { id: "sum", label: "Sum" },
  { id: "avg", label: "Average" },
  { id: "count", label: "Count" },
];

export const ABC_PERIOD_OPTIONS = [
  { id: "last_12_months", label: "Last 12 months" },
  { id: "fiscal_year", label: "Financial year" },
];

// Financial years rarely run January-December in manufacturing, so the opening
// month is a setting rather than an assumption.
export const FISCAL_YEAR_START_MONTHS = [
  { id: 1, label: "January" },
  { id: 2, label: "February" },
  { id: 3, label: "March" },
  { id: 4, label: "April" },
  { id: 5, label: "May" },
  { id: 6, label: "June" },
  { id: 7, label: "July" },
  { id: 8, label: "August" },
  { id: 9, label: "September" },
  { id: 10, label: "October" },
  { id: 11, label: "November" },
  { id: 12, label: "December" },
];

export const XYZ_BASIS_OPTIONS = [
  { id: "count", label: "Count per period" },
  { id: "value", label: "Value per period" },
];

// Bucket size the Analysis Period (Time Period) is split into before
// computing the coefficient of variation. Deliberately Week/Month only — no
// Quarter — so the calculation always matches exactly what was selected.
export const XYZ_GRANULARITY_OPTIONS = [
  { id: "week", label: "Weeks" },
  { id: "month", label: "Months" },
];

// Reserved ABC metric meaning "classify by object count per group" —
// mirrors the backend's COUNT_METRIC.
export const COUNT_METRIC = "count";

// Matrix row headers: "letter (name)" per analysis mode — variants are
// ranked by case volume, object groups by the selected metric. The 80/95
// Pareto rule is explained in the info tooltip.
export const ABC_ROW_HEADERS = {
  A: { variant: "High Volume", object: "High Impact" },
  B: { variant: "Medium Volume", object: "Medium Impact" },
  C: { variant: "Low Volume", object: "Low Impact" },
};

export const XYZ_COLUMN_LABELS = {
  X: "Stable",
  Y: "Varying",
  Z: "Sporadic",
};

export const XYZ_BEHAVIOR_DESCRIPTIONS = {
  X: "runs regularly",
  Y: "fluctuates",
  Z: "occurs irregularly",
};

// Cross matrix axis headers: rows are object importance, columns variant
// importance. Both axes are ABC axes, so they reuse the ABC row headers
// verbatim rather than restating them — one source of truth for the wording.
export const CROSS_ROW_HEADERS = Object.fromEntries(
  Object.entries(ABC_ROW_HEADERS).map(([abc, names]) => [abc, names.object]),
);
export const CROSS_COLUMN_HEADERS = Object.fromEntries(
  Object.entries(ABC_ROW_HEADERS).map(([abc, names]) => [abc, names.variant]),
);

export const VARIANT_MATRIX_TOOLTIP =
  "Rows show how much case volume each process variant covers (A/B/C), " +
  "columns show how regularly that variant occurs over time (X/Y/Z). " +
  "Each cell names its class; its colour shows how predictable that " +
  "variant is.";
export const OBJECT_MATRIX_TOOLTIP =
  "Rows show how much impact each object group has (A/B/C), columns show " +
  "how regularly that group's activity occurs over time (X/Y/Z). Each " +
  "cell names its class; its colour shows how predictable that group is.";
export const ABC_TOOLTIP =
  "Ranks by volume, value or your chosen measure. A = top 80 % of the " +
  "total, B = next 15 %, C = last 5 %.";
export const XYZ_TOOLTIP =
  "How predictable something is over time, based on the coefficient of " +
  "variation of its per-period values. X = stable (under 25 %), " +
  "Y = varying (25–50 %), Z = sporadic (over 50 %).";
export const CV_TOOLTIP_LINES = [
  {
    label: "Variability",
    text:
      "How much order or case activity swings over time, relative to " +
      "the average. Lower is steady; higher is bursty.",
  },
  {
    label: "Scale",
    text:
      "Stable under 25 %, Varying 25–50 %, Sporadic above 50 % — the " +
      "same bands used in the matrix below.",
  },
  {
    label: "Example",
    text:
      "20 % is steady; 120 % means the swings are bigger than the " +
      "average itself.",
  },
];
export const ANALYSIS_PERIOD_TOOLTIP =
  "How far back the ranking looks: the last 12 months, or your financial " +
  "year. Both are measured from the most recent date in your data, not " +
  "today.";
export const CALCULATION_GRANULARITY_TOOLTIP =
  "How the Analysis Period is split before measuring variability: weekly " +
  "or monthly buckets. Analysis Period sets how much history is used; " +
  "Calculation Granularity sets how that history is divided up.";
export const TOTAL_METRIC_VALUE_TOOLTIP =
  "Sum of the selected metric across all classified groups. With Sum " +
  "aggregation this is the true grand total; with Average, each group is " +
  "averaged first, so the total is a sum of group averages, not of every " +
  "object.";
export const CROSS_TOOLTIP =
  "Rows show object importance, columns show process-path importance, for " +
  "the same underlying Object × Variant combinations. Each cell names the " +
  "recommended action. Position shows importance; colour shows stability — " +
  "so an A×A cell can still be red/Z when those high-importance " +
  "combinations occur irregularly.";
